/**
 * Onboarding modal wrapper containing logic
 * to display each step of the onboarding
 * process. Also initiates and kills polling
 * for the example cURL test job create step.
 */

import React, { FC, useCallback } from "react"
import { useDispatch } from "react-redux"
import {
    onboardingForward,
    onboardingBack,
    toggleDemoMode,
    setOnboardingLabeler,
    setSampleAppIdentifier,
    killOnboardingPolls
} from "../../../../../Actions/API/AppTemplate/Interface/TemplateInstantiateActions"
import { Loader } from "dash-styled"
import { TemplateSlug } from "../../../../../Models/API/AppTemplateModel"
import { SetUserStage } from "./Stages/SetUser/SetUser"
import { SetParametersStage } from "./Stages/SetParameters/SetParameters"
import { CreateJobStage, NoSDKInput } from "./Stages/CreateJob/CreateJob"
import { hasValue } from "../../../../../Models/Client/FetchState"
import { getValueById } from "../../../../../Models/Client/RemoteData"
import { TemplateInstantiateStage } from "../../../../../Models/Client/TemplateInstantiation"
import { selectLastJobID } from "../../../../../Helpers/Functions/Selectors/onboarding"
import {
    selectIsSysAdmin,
    selectUserName,
    selectUser
} from "../../../../../Helpers/Functions/Selectors/auth"
import { Redirect } from "react-router"
import { routePaths } from "../../../../../Routes"
import { push } from "connected-react-router"
import { useAppStatus } from "../../../../../Hooks/useAppStatus"
import { JobSubmitData } from "../../../../../Models/Client/JobSubmitData"
import { useAppState } from "../../../../../Hooks/useReduxState"
import { useErrorTrackingInfo } from "../../../../../Hooks/useErrorTrackingInfo"
import { SelectLabelerStage } from "./Stages/SelectLabeler/SelectLabeler"
import { PrepareLabeling } from "./Stages/PrepareLabeling/PrepareLabeling"
import { useTemplateFromList } from "../../../../../Hooks/useTemplateFromList"
import { SelectProjectStage } from "./Stages/SelectProject/SelectProject"

interface OnboardingProps {
    slug: TemplateSlug
    stage: TemplateInstantiateStage
    mode?: NoSDKInput
    isJobCorrect?: boolean
}

export const Onboarding: FC<OnboardingProps> = ({ stage, slug, mode }) => {
    const onboarding = useAppState(
        state => state.api.interface.templateInstantiate
    )
    const templateIdentifier = onboarding?.templateIdentifier
    const templateTitle = onboarding?.templateTitle
    const instance = useAppState(state =>
        onboarding?.instanceID === undefined
            ? undefined
            : getValueById(state.api.apps.apps, onboarding.instanceID)
    )
    const jobID = useAppState(selectLastJobID)
    const user = useAppState(selectUser)
    const inputForm = useAppState(state =>
        onboarding?.inputDataRenderId === undefined
            ? undefined
            : state.client.inputRender.entries[onboarding.inputDataRenderId]
    )
    const inputJobSubmitData =
        inputForm?.values === undefined
            ? undefined
            : (inputForm.values as JobSubmitData)

    const inputData =
        inputJobSubmitData?.type === "raw"
            ? inputJobSubmitData.jobs[0]?.input
            : undefined

    const isSubmittingJob = onboarding?.submittingJob
    const canoticCrowd = onboarding?.canoticCrowd
    const sampleApps = onboarding?.sampleApps
    const useName = useAppState(selectUserName)
    const orgUsername = onboarding?.orgUsername ?? useName
    const aiWorkflow = onboarding?.aiWorkflow
    const dispatch = useDispatch()

    const forward = useCallback(
        () => dispatch(onboardingForward(stage)),
        [dispatch, stage]
    )
    const back = useCallback(
        () => dispatch(onboardingBack(stage)),
        [dispatch, stage]
    )
    const changeInputMode = useCallback(
        (button: NoSDKInput) =>
            dispatch(push(routePaths.onboardingCreateJob(slug, button))),
        [dispatch, slug]
    )

    const handleToggleDemoMode = useCallback(
        () => dispatch(toggleDemoMode()),
        [dispatch]
    )
    const handleOnboardingLabeler = useCallback(
        (canoticCrowd: boolean) => dispatch(setOnboardingLabeler(canoticCrowd)),
        [dispatch]
    )
    const handleSampleAppIdentifier = useCallback(
        (selectedSampleAppIdentifier?: string) =>
            dispatch(setSampleAppIdentifier(selectedSampleAppIdentifier)),
        [dispatch]
    )
    const openLabelingUI = useCallback(
        (orgUsername: string, uuid: string) =>
            dispatch(
                push(routePaths.appDetailLabeling(orgUsername, uuid), {
                    onboarding: true,
                    backPath: routePaths.onboardingSetParameters(slug)
                })
            ),
        [dispatch, slug]
    )
    const killOnboardPolling = useCallback(
        () => dispatch(killOnboardingPolls()),
        [dispatch]
    )
    useErrorTrackingInfo("superai.appID", instance?.uuid)
    useErrorTrackingInfo("superai.templateName", templateIdentifier)

    const isAppStatusReady = hasValue(
        useAppStatus(instance === undefined ? undefined : instance.uuid)
    )

    const isSysAdmin = useAppState(selectIsSysAdmin)
    const templateData = useTemplateFromList(templateIdentifier)

    if (templateIdentifier === undefined || templateTitle === undefined) {
        // these values are always set before opening modal.
        return <Redirect to={routePaths.onboardingChooseOrganization(slug)} />
    }

    const render = () => {
        if (!hasValue(templateData) || onboarding === null) {
            return <Loader />
        }

        const template = templateData.value

        switch (stage) {
            case TemplateInstantiateStage.setUser:
                return (
                    <SetUserStage
                        onForward={forward}
                        loginRoute={routePaths.onboardingLoginUser(slug)}
                    />
                )
            case TemplateInstantiateStage.loginUser:
                return (
                    <SetUserStage
                        onForward={forward}
                        loginRoute={routePaths.onboardingLoginUser(slug)}
                        login={true}
                    />
                )
            case TemplateInstantiateStage.setParameters:
                return (
                    <SetParametersStage
                        template={template}
                        formID={onboarding.paramsRenderId}
                        onBack={!isSubmittingJob ? back : undefined}
                        onForward={!isSubmittingJob ? forward : undefined}
                        inputData={
                            inputData ??
                            templateData.value.defaultAppParams?.params
                        }
                        demo={
                            !isSysAdmin || !canoticCrowd || aiWorkflow === true
                                ? undefined
                                : {
                                      active: onboarding.demo,
                                      toggle: handleToggleDemoMode
                                  }
                        }
                    />
                )
            case TemplateInstantiateStage.createJob:
                if (mode === undefined || user === undefined) return <Loader />
                return (
                    <CreateJobStage
                        key={mode}
                        orgUsername={orgUsername ?? user.username}
                        template={template}
                        formID={onboarding?.inputDataRenderId}
                        onBack={back}
                        back={!!sampleApps}
                        onForward={forward}
                        mode={mode}
                        changeMode={changeInputMode}
                    />
                )
            case TemplateInstantiateStage.selectLabeler:
                return (
                    <SelectLabelerStage
                        onForward={forward}
                        onBack={back}
                        setLabeler={handleOnboardingLabeler}
                    />
                )
            case TemplateInstantiateStage.selectProject:
                if (sampleApps === undefined) return <Loader />
                return (
                    <SelectProjectStage
                        setSampleAppIdentifier={handleSampleAppIdentifier}
                        onForward={forward}
                        onBack={back}
                        sampleApps={sampleApps}
                        paramsFormId={onboarding.paramsRenderId}
                        inputFormId={onboarding.inputDataRenderId}
                    />
                )
            case TemplateInstantiateStage.prepareLabeling:
                if (user === undefined) return <Loader />
                return (
                    <PrepareLabeling
                        orgUsername={orgUsername ?? user.username}
                        onForward={
                            instance === undefined || !isAppStatusReady
                                ? undefined
                                : forward
                        }
                        onBack={back}
                        instance={instance}
                        jobID={jobID}
                        openLabelingUI={openLabelingUI}
                        templateTitle={templateTitle}
                        killOnboardPolling={killOnboardPolling}
                    />
                )
            default:
                return <Loader />
        }
    }

    return render()
}
